import { authConfig } from "../auth/cognitoAuth";

const fallbackRegion = "us-west-2";

const getRegionFromDomain = (domain) => {
    const match = domain.match(/\.auth\.([a-z0-9-]+)\.amazoncognito\.com/i);
    return match ? match[1] : fallbackRegion;
};

const getCognitoDomain = () =>
    authConfig.domain || "https://<auth-domain>.auth.<aws-region>.amazoncognito.com";

const getClientId = () => authConfig.clientId || "<auth-client-id>";

const labels = {
    es: {
        title: "Auth - Generacion de access token",
        description:
            "Contrato para obtener el access_token que se usa en los servicios del sandbox con el header Authorization: Bearer <access_token>.",
        serverDescription: "Proveedor de identidad",
        tagDescription: "Generacion del token usado por API Gateway para autorizar llamadas al sandbox.",
        summary: "Generar access token con usuario y password",
        operationDescription:
            "Usa USER_PASSWORD_AUTH para obtener AuthenticationResult.AccessToken. Ese valor es el mismo bearer que debe enviarse en Authorization para consumir RENAPO, NOM151, Lista Nominal y los demas servicios protegidos. X-Amz-Target, Content-Type, AuthFlow y ClientId no son secretos; las credenciales USERNAME/PASSWORD y los JWT emitidos si deben tratarse como secretos.",
        targetDescription:
            "Header fijo del proveedor de autenticacion. Copialo tal cual; no es un secreto.",
        contentTypeDescription:
            "Content type requerido por el proveedor de autenticacion. Copialo tal cual; no es un secreto.",
        passwordExample: "<password-del-cliente>",
        successDescription: "Token generado correctamente.",
        badRequestDescription:
            "Solicitud invalida, credenciales incorrectas, usuario con reto pendiente o App Client no habilitado para USER_PASSWORD_AUTH.",
        authFlowDescription:
            "Flujo de autenticacion habilitado para este cliente. Es un valor publico y fijo para este contrato.",
        clientIdDescription:
            "Identificador publico del cliente de autenticacion. No es un secreto porque el cliente esta configurado sin client secret.",
        authParametersDescription:
            "Credenciales del usuario sandbox. Estos valores si son sensibles y deben viajar solo por HTTPS.",
        usernameDescription: "Usuario asignado al cliente del sandbox.",
        passwordDescription:
            "Password temporal o definitivo del usuario. Debe mantenerse fuera de repositorios, logs y clientes no confiables.",
        accessTokenDescription: "JWT que se envia como Authorization: Bearer <access_token>.",
        challengeDescription:
            "Si el proveedor responde NEW_PASSWORD_REQUIRED, completa el cambio de password antes de usar el token.",
    },
    en: {
        title: "Auth - Access Token Generation",
        description:
            "Contract for obtaining the access_token used by the sandbox services with the Authorization: Bearer <access_token> header.",
        serverDescription: "Identity Provider",
        tagDescription: "Token generation used by API Gateway to authorize sandbox requests.",
        summary: "Generate an access token with username and password",
        operationDescription:
            "Use USER_PASSWORD_AUTH to obtain AuthenticationResult.AccessToken. That value is the same bearer token sent in Authorization to consume RENAPO, NOM151, Nominal List, and the other protected services. X-Amz-Target, Content-Type, AuthFlow, and ClientId are not secrets; USERNAME/PASSWORD credentials and issued JWTs must be treated as secrets.",
        targetDescription:
            "Fixed authentication provider header. Copy it as-is; it is not a secret.",
        contentTypeDescription:
            "Content type required by the authentication provider. Copy it as-is; it is not a secret.",
        passwordExample: "<client-password>",
        successDescription: "Token generated successfully.",
        badRequestDescription:
            "Invalid request, incorrect credentials, pending user challenge, or App Client not enabled for USER_PASSWORD_AUTH.",
        authFlowDescription:
            "Authentication flow enabled for this client. It is a public and fixed value for this contract.",
        clientIdDescription:
            "Public authentication client identifier. It is not a secret because the client is configured without a client secret.",
        authParametersDescription:
            "Sandbox user credentials. These values are sensitive and must only be sent over HTTPS.",
        usernameDescription: "User assigned to the sandbox client.",
        passwordDescription:
            "Temporary or definitive user password. Keep it out of repositories, logs, and untrusted clients.",
        accessTokenDescription: "JWT sent as Authorization: Bearer <access_token>.",
        challengeDescription:
            "If the provider responds with NEW_PASSWORD_REQUIRED, complete the password change before using the token.",
    },
};

export const createCognitoTokenOpenApi = (language = "es") => {
    const copy = labels[language] || labels.es;
    const cognitoDomain = getCognitoDomain();
    const region = getRegionFromDomain(cognitoDomain);
    const clientId = getClientId();

    return {
        openapi: "3.0.3",
        info: {
            title: copy.title,
            description: copy.description,
            version: "1.0.0",
        },
        servers: [
            {
                url: `https://cognito-idp.${region}.amazonaws.com`,
                description: copy.serverDescription,
            },
        ],
        tags: [
            {
                name: "Auth",
                description: copy.tagDescription,
            },
        ],
        paths: {
            "/": {
                post: {
                    tags: ["Auth"],
                    summary: copy.summary,
                    description: copy.operationDescription,
                    operationId: "initiateAuthUserPassword",
                    servers: [
                        {
                            url: `https://cognito-idp.${region}.amazonaws.com`,
                        },
                    ],
                    parameters: [
                        {
                            name: "X-Amz-Target",
                            in: "header",
                            required: true,
                            description: copy.targetDescription,
                            schema: {
                                type: "string",
                                default: "AWSCognitoIdentityProviderService.InitiateAuth",
                            },
                        },
                        {
                            name: "Content-Type",
                            in: "header",
                            required: true,
                            description: copy.contentTypeDescription,
                            schema: {
                                type: "string",
                                default: "application/x-amz-json-1.1",
                            },
                        },
                    ],
                    requestBody: {
                        required: true,
                        content: {
                            "application/x-amz-json-1.1": {
                                schema: {
                                    $ref: "#/components/schemas/InitiateAuthRequest",
                                },
                                example: {
                                    AuthFlow: "USER_PASSWORD_AUTH",
                                    ClientId: clientId,
                                    AuthParameters: {
                                        USERNAME: "cliente-demo",
                                        PASSWORD: copy.passwordExample,
                                    },
                                },
                            },
                        },
                    },
                    responses: {
                        200: {
                            description: copy.successDescription,
                            content: {
                                "application/x-amz-json-1.1": {
                                    schema: {
                                        $ref: "#/components/schemas/InitiateAuthResponse",
                                    },
                                },
                                "application/json": {
                                    schema: {
                                        $ref: "#/components/schemas/InitiateAuthResponse",
                                    },
                                },
                            },
                        },
                        400: {
                            description: copy.badRequestDescription,
                            content: {
                                "application/x-amz-json-1.1": {
                                    schema: {
                                        $ref: "#/components/schemas/AuthError",
                                    },
                                },
                                "application/json": {
                                    schema: {
                                        $ref: "#/components/schemas/AuthError",
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        components: {
            schemas: {
                InitiateAuthRequest: {
                    type: "object",
                    required: ["AuthFlow", "ClientId", "AuthParameters"],
                    properties: {
                        AuthFlow: {
                            type: "string",
                            description: copy.authFlowDescription,
                            enum: ["USER_PASSWORD_AUTH"],
                            example: "USER_PASSWORD_AUTH",
                        },
                        ClientId: {
                            type: "string",
                            description: copy.clientIdDescription,
                            example: clientId,
                        },
                        AuthParameters: {
                            type: "object",
                            required: ["USERNAME", "PASSWORD"],
                            description: copy.authParametersDescription,
                            properties: {
                                USERNAME: {
                                    type: "string",
                                    description: copy.usernameDescription,
                                    example: "cliente-demo",
                                },
                                PASSWORD: {
                                    type: "string",
                                    format: "password",
                                    description: copy.passwordDescription,
                                    example: copy.passwordExample,
                                },
                            },
                        },
                    },
                },
                InitiateAuthResponse: {
                    type: "object",
                    properties: {
                        AuthenticationResult: {
                            type: "object",
                            properties: {
                                AccessToken: {
                                    type: "string",
                                    description: copy.accessTokenDescription,
                                },
                                IdToken: {
                                    type: "string",
                                },
                                RefreshToken: {
                                    type: "string",
                                },
                                ExpiresIn: {
                                    type: "integer",
                                    example: 900,
                                },
                                TokenType: {
                                    type: "string",
                                    example: "Bearer",
                                },
                            },
                        },
                        ChallengeName: {
                            type: "string",
                            description: copy.challengeDescription,
                            example: "NEW_PASSWORD_REQUIRED",
                        },
                        Session: {
                            type: "string",
                        },
                    },
                },
                AuthError: {
                    type: "object",
                    properties: {
                        __type: {
                            type: "string",
                            example: "NotAuthorizedException",
                        },
                        message: {
                            type: "string",
                        },
                    },
                },
            },
        },
    };
};
